import type { TFunction } from '@unifyapps/i18n/client';
import { colorGroups as chartColorGroups } from '@unifyapps/defs/blocks/Chart/chartColorGroups';
import { backgroundColorGroups } from '@unifyapps/defs/constants/backgroundColorGroups';
import { ApplicationType } from '@unifyapps/defs/types/interface';
import type { ColorGroups } from '../../../../components/ColorPicker';
import type { AppearanceStyle, StyleSizeOption } from './types';
import { borderColorGroups } from './color';

export const getGroupNameFromGroupId = ({ groupId, t }: { groupId: string; t: TFunction }) => {
  switch (groupId) {
    case 'NEGATIVE':
      return t('interfaces:Styles.NegativeValues');
    default:
      return groupId;
  }
};

const getMarginConfig = (i18nextT: TFunction, applicationType: ApplicationType | undefined) => ({
  isDirectional: true,
  options: {
    all: [
      { id: 'm-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'm-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'm-xs', name: 'xs', styleValue: '4px' },
      { id: 'm-sm', name: 'sm', styleValue: '6px' },
      { id: 'm-md', name: 'md', styleValue: '8px' },
      { id: 'm-lg', name: 'lg', styleValue: '12px' },
      { id: 'm-xl', name: 'xl', styleValue: '16px' },
      { id: 'm-2xl', name: '2xl', styleValue: '20px' },
      { id: 'm-3xl', name: '3xl', styleValue: '24px' },
      { id: 'm-4xl', name: '4xl', styleValue: '32px' },
      { id: 'm-5xl', name: '5xl', styleValue: '40px' },
      { id: 'm-[48px]', name: '6xl', styleValue: '48px' },
      { id: 'm-[84px]', name: '84px', styleValue: '84px' },
      { id: 'm-9xl', name: '9xl', styleValue: '96px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'm-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: 'm-auto', name: 'Auto' },
      { id: '-m-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-m-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-m-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-m-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-m-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-m-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-m-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-m-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-m-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-m-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: '-m-6xl', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: 'm-[-84px]', name: '84px', styleValue: '-84px', groupId: 'NEGATIVE' },
      { id: '-m-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
    ],
    x: [
      { id: 'mx-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'mx-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'mx-xs', name: 'xs', styleValue: '4px' },
      { id: 'mx-sm', name: 'sm', styleValue: '6px' },
      { id: 'mx-md', name: 'md', styleValue: '8px' },
      { id: 'mx-lg', name: 'lg', styleValue: '12px' },
      { id: 'mx-xl', name: 'xl', styleValue: '16px' },
      { id: 'mx-2xl', name: '2xl', styleValue: '20px' },
      { id: 'mx-3xl', name: '3xl', styleValue: '24px' },
      { id: 'mx-4xl', name: '4xl', styleValue: '32px' },
      { id: 'mx-5xl', name: '5xl', styleValue: '40px' },
      { id: 'mx-[48px]', name: '6xl', styleValue: '48px' },
      { id: 'mx-[84px]', name: '84px', styleValue: '84px' },
      { id: 'mx-9xl', name: '9xl', styleValue: '96px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'mx-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: 'mx-auto', name: 'Auto' },
      { id: '-mx-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-mx-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-mx-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-mx-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-mx-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-mx-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-mx-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-mx-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-mx-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-mx-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: 'mx-[-48px]', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: 'mx-[-84px]', name: '84px', styleValue: '-84px', groupId: 'NEGATIVE' },
      { id: '-mx-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
    ],
    y: [
      { id: 'my-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'my-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'my-xs', name: 'xs', styleValue: '4px' },
      { id: 'my-sm', name: 'sm', styleValue: '6px' },
      { id: 'my-md', name: 'md', styleValue: '8px' },
      { id: 'my-lg', name: 'lg', styleValue: '12px' },
      { id: 'my-xl', name: 'xl', styleValue: '16px' },
      { id: 'my-2xl', name: '2xl', styleValue: '20px' },
      { id: 'my-3xl', name: '3xl', styleValue: '24px' },
      { id: 'my-4xl', name: '4xl', styleValue: '32px' },
      { id: 'my-5xl', name: '5xl', styleValue: '40px' },
      { id: 'my-[48px]', name: '6xl', styleValue: '48px' },
      { id: 'my-[84px]', name: '84px', styleValue: '84px' },
      { id: 'my-9xl', name: '9xl', styleValue: '96px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'my-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: 'my-auto', name: 'Auto' },
      { id: '-my-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-my-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-my-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-my-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-my-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-my-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-my-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-my-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-my-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-my-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: 'my-[-48px]', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: 'my-[-84px]', name: '84px', styleValue: '-84px', groupId: 'NEGATIVE' },
      { id: '-my-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
    ],
    t: [
      { id: 'mt-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'mt-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'mt-xs', name: 'xs', styleValue: '4px' },
      { id: 'mt-sm', name: 'sm', styleValue: '6px' },
      { id: 'mt-md', name: 'md', styleValue: '8px' },
      { id: 'mt-lg', name: 'lg', styleValue: '12px' },
      { id: 'mt-xl', name: 'xl', styleValue: '16px' },
      { id: 'mt-2xl', name: '2xl', styleValue: '20px' },
      { id: 'mt-3xl', name: '3xl', styleValue: '24px' },
      { id: 'mt-4xl', name: '4xl', styleValue: '32px' },
      { id: 'mt-5xl', name: '5xl', styleValue: '40px' },
      { id: 'mt-[48px]', name: '6xl', styleValue: '48px' },
      { id: 'mt-[84px]', name: '84px', styleValue: '84px' },
      { id: 'mt-9xl', name: '9xl', styleValue: '96px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'mt-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: 'mt-auto', name: 'Auto' },
      { id: '-mt-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-mt-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-mt-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-mt-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-mt-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-mt-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-mt-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-mt-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-mt-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-mt-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: 'mt-[-48px]', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: 'mt-[-84px]', name: '84px', styleValue: '-84px', groupId: 'NEGATIVE' },
      { id: '-mt-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
    ],
    r: [
      { id: 'me-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'me-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'me-xs', name: 'xs', styleValue: '4px' },
      { id: 'me-sm', name: 'sm', styleValue: '6px' },
      { id: 'me-md', name: 'md', styleValue: '8px' },
      { id: 'me-lg', name: 'lg', styleValue: '12px' },
      { id: 'me-xl', name: 'xl', styleValue: '16px' },
      { id: 'me-2xl', name: '2xl', styleValue: '20px' },
      { id: 'me-3xl', name: '3xl', styleValue: '24px' },
      { id: 'me-4xl', name: '4xl', styleValue: '32px' },
      { id: 'me-5xl', name: '5xl', styleValue: '40px' },
      { id: 'me-[48px]', name: '6xl', styleValue: '48px' },
      { id: 'me-[84px]', name: '84px', styleValue: '84px' },
      { id: 'me-9xl', name: '9xl', styleValue: '96px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'me-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: 'me-auto', name: 'Auto' },
      { id: '-me-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-me-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-me-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-me-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-me-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-me-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-me-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-me-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-me-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-me-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: 'me-[-48px]', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: 'me-[-84px]', name: '84px', styleValue: '-84px', groupId: 'NEGATIVE' },
      { id: '-me-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
    ],
    b: [
      { id: 'mb-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'mb-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'mb-xs', name: 'xs', styleValue: '4px' },
      { id: 'mb-sm', name: 'sm', styleValue: '6px' },
      { id: 'mb-md', name: 'md', styleValue: '8px' },
      { id: 'mb-lg', name: 'lg', styleValue: '12px' },
      { id: 'mb-xl', name: 'xl', styleValue: '16px' },
      { id: 'mb-2xl', name: '2xl', styleValue: '20px' },
      { id: 'mb-3xl', name: '3xl', styleValue: '24px' },
      { id: 'mb-4xl', name: '4xl', styleValue: '32px' },
      { id: 'mb-5xl', name: '5xl', styleValue: '40px' },
      { id: 'mb-[48px]', name: '6xl', styleValue: '48px' },
      { id: 'mb-[84px]', name: '84px', styleValue: '84px' },
      { id: 'mb-9xl', name: '9xl', styleValue: '96px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'mb-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: 'mb-auto', name: 'Auto' },
      { id: '-mb-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-mb-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-mb-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-mb-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-mb-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-mb-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-mb-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-mb-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-mb-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-mb-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: 'mb-[-48px]', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: 'mb-[-84px]', name: '84px', styleValue: '-84px', groupId: 'NEGATIVE' },
      { id: '-mb-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
    ],
    l: [
      { id: 'ms-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'ms-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'ms-xs', name: 'xs', styleValue: '4px' },
      { id: 'ms-sm', name: 'sm', styleValue: '6px' },
      { id: 'ms-md', name: 'md', styleValue: '8px' },
      { id: 'ms-lg', name: 'lg', styleValue: '12px' },
      { id: 'ms-xl', name: 'xl', styleValue: '16px' },
      { id: 'ms-2xl', name: '2xl', styleValue: '20px' },
      { id: 'ms-3xl', name: '3xl', styleValue: '24px' },
      { id: 'ms-4xl', name: '4xl', styleValue: '32px' },
      { id: 'ms-5xl', name: '5xl', styleValue: '40px' },
      { id: 'ms-[48px]', name: '6xl', styleValue: '48px' },
      { id: 'ms-[84px]', name: '84px', styleValue: '84px' },
      { id: 'ms-9xl', name: '9xl', styleValue: '96px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'ms-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: 'ms-auto', name: 'Auto' },
      { id: '-ms-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-ms-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-ms-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-ms-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-ms-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-ms-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-ms-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-ms-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-ms-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-ms-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: 'ms-[-48px]', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: 'ms-[-84px]', name: '84px', styleValue: '-84px', groupId: 'NEGATIVE' },
      { id: '-ms-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
    ],
  },
});

const getPaddingConfig = (i18nextT: TFunction, applicationType: ApplicationType | undefined) => ({
  isDirectional: true,
  options: {
    all: [
      { id: 'p-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'p-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'p-xs', name: 'xs', styleValue: '4px' },
      { id: 'p-sm', name: 'sm', styleValue: '6px' },
      { id: 'p-md', name: 'md', styleValue: '8px' },
      { id: 'p-lg', name: 'lg', styleValue: '12px' },
      { id: 'p-xl', name: 'xl', styleValue: '16px' },
      { id: 'p-2xl', name: '2xl', styleValue: '20px' },
      { id: 'p-3xl', name: '3xl', styleValue: '24px' },
      { id: 'p-4xl', name: '4xl', styleValue: '32px' },
      { id: 'p-5xl', name: '5xl', styleValue: '40px' },
      { id: 'p-6xl', name: '6xl', styleValue: '48px' },
      { id: 'p-7xl', name: '7xl', styleValue: '64px' },
      { id: 'p-8xl', name: '8xl', styleValue: '80px' },
      { id: 'p-9xl', name: '9xl', styleValue: '96px' },
      { id: 'p-10xl', name: '10xl', styleValue: '128px' },
      { id: 'p-11xl', name: '11xl', styleValue: '160px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'p-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: '-p-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-p-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-p-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-p-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-p-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-p-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-p-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-p-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-p-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-p-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: '-p-6xl', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: '-p-7xl', name: '7xl', styleValue: '-64px', groupId: 'NEGATIVE' },
      { id: '-p-8xl', name: '8xl', styleValue: '-80px', groupId: 'NEGATIVE' },
      { id: '-p-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
      { id: '-p-10xl', name: '10xl', styleValue: '-128px', groupId: 'NEGATIVE' },
      { id: '-p-11xl', name: '11xl', styleValue: '-160px', groupId: 'NEGATIVE' },
    ],
    x: [
      { id: 'px-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'px-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'px-xs', name: 'xs', styleValue: '4px' },
      { id: 'px-sm', name: 'sm', styleValue: '6px' },
      { id: 'px-md', name: 'md', styleValue: '8px' },
      { id: 'px-lg', name: 'lg', styleValue: '12px' },
      { id: 'px-xl', name: 'xl', styleValue: '16px' },
      { id: 'px-2xl', name: '2xl', styleValue: '20px' },
      { id: 'px-3xl', name: '3xl', styleValue: '24px' },
      { id: 'px-4xl', name: '4xl', styleValue: '32px' },
      { id: 'px-5xl', name: '5xl', styleValue: '40px' },
      { id: 'px-6xl', name: '6xl', styleValue: '48px' },
      { id: 'px-7xl', name: '7xl', styleValue: '64px' },
      { id: 'px-8xl', name: '8xl', styleValue: '80px' },
      { id: 'px-9xl', name: '9xl', styleValue: '96px' },
      { id: 'px-10xl', name: '10xl', styleValue: '128px' },
      { id: 'px-11xl', name: '11xl', styleValue: '160px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'px-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: '-px-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-px-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-px-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-px-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-px-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-px-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-px-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-px-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-px-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-px-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: '-px-6xl', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: '-px-7xl', name: '7xl', styleValue: '-64px', groupId: 'NEGATIVE' },
      { id: '-px-8xl', name: '8xl', styleValue: '-80px', groupId: 'NEGATIVE' },
      { id: '-px-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
      { id: '-px-10xl', name: '10xl', styleValue: '-128px', groupId: 'NEGATIVE' },
      { id: '-px-11xl', name: '11xl', styleValue: '-160px', groupId: 'NEGATIVE' },
    ],
    y: [
      { id: 'py-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'py-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'py-xs', name: 'xs', styleValue: '4px' },
      { id: 'py-sm', name: 'sm', styleValue: '6px' },
      { id: 'py-md', name: 'md', styleValue: '8px' },
      { id: 'py-lg', name: 'lg', styleValue: '12px' },
      { id: 'py-xl', name: 'xl', styleValue: '16px' },
      { id: 'py-2xl', name: '2xl', styleValue: '20px' },
      { id: 'py-3xl', name: '3xl', styleValue: '24px' },
      { id: 'py-4xl', name: '4xl', styleValue: '32px' },
      { id: 'py-5xl', name: '5xl', styleValue: '40px' },
      { id: 'py-6xl', name: '6xl', styleValue: '48px' },
      { id: 'py-7xl', name: '7xl', styleValue: '64px' },
      { id: 'py-8xl', name: '8xl', styleValue: '80px' },
      { id: 'py-9xl', name: '9xl', styleValue: '96px' },
      { id: 'py-10xl', name: '10xl', styleValue: '128px' },
      { id: 'py-11xl', name: '11xl', styleValue: '160px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'py-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: '-py-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-py-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-py-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-py-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-py-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-py-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-py-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-py-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-py-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-py-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: '-py-6xl', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: '-py-7xl', name: '7xl', styleValue: '-64px', groupId: 'NEGATIVE' },
      { id: '-py-8xl', name: '8xl', styleValue: '-80px', groupId: 'NEGATIVE' },
      { id: '-py-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
      { id: '-py-10xl', name: '10xl', styleValue: '-128px', groupId: 'NEGATIVE' },
      { id: '-py-11xl', name: '11xl', styleValue: '-160px', groupId: 'NEGATIVE' },
    ],
    t: [
      { id: 'pt-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'pt-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'pt-xs', name: 'xs', styleValue: '4px' },
      { id: 'pt-sm', name: 'sm', styleValue: '6px' },
      { id: 'pt-md', name: 'md', styleValue: '8px' },
      { id: 'pt-lg', name: 'lg', styleValue: '12px' },
      { id: 'pt-xl', name: 'xl', styleValue: '16px' },
      { id: 'pt-2xl', name: '2xl', styleValue: '20px' },
      { id: 'pt-3xl', name: '3xl', styleValue: '24px' },
      { id: 'pt-4xl', name: '4xl', styleValue: '32px' },
      { id: 'pt-5xl', name: '5xl', styleValue: '40px' },
      { id: 'pt-6xl', name: '6xl', styleValue: '48px' },
      { id: 'pt-7xl', name: '7xl', styleValue: '64px' },
      { id: 'pt-8xl', name: '8xl', styleValue: '80px' },
      { id: 'pt-9xl', name: '9xl', styleValue: '96px' },
      { id: 'pt-10xl', name: '10xl', styleValue: '128px' },
      { id: 'pt-[156px]', name: '156px', styleValue: '156px' },
      { id: 'pt-11xl', name: '11xl', styleValue: '160px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'pt-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: '-pt-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-pt-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-pt-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-pt-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-pt-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-pt-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-pt-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-pt-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-pt-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-pt-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: '-pt-6xl', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: '-pt-7xl', name: '7xl', styleValue: '-64px', groupId: 'NEGATIVE' },
      { id: '-pt-8xl', name: '8xl', styleValue: '-80px', groupId: 'NEGATIVE' },
      { id: '-pt-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
      { id: '-pt-10xl', name: '10xl', styleValue: '-128px', groupId: 'NEGATIVE' },
      { id: '-pt-11xl', name: '11xl', styleValue: '-160px', groupId: 'NEGATIVE' },
    ],
    r: [
      { id: 'pe-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'pe-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'pe-xs', name: 'xs', styleValue: '4px' },
      { id: 'pe-sm', name: 'sm', styleValue: '6px' },
      { id: 'pe-md', name: 'md', styleValue: '8px' },
      { id: 'pe-lg', name: 'lg', styleValue: '12px' },
      { id: 'pe-xl', name: 'xl', styleValue: '16px' },
      { id: 'pe-2xl', name: '2xl', styleValue: '20px' },
      { id: 'pe-3xl', name: '3xl', styleValue: '24px' },
      { id: 'pe-4xl', name: '4xl', styleValue: '32px' },
      { id: 'pe-5xl', name: '5xl', styleValue: '40px' },
      { id: 'pe-6xl', name: '6xl', styleValue: '48px' },
      { id: 'pe-7xl', name: '7xl', styleValue: '64px' },
      { id: 'pe-8xl', name: '8xl', styleValue: '80px' },
      { id: 'pe-9xl', name: '9xl', styleValue: '96px' },
      { id: 'pe-10xl', name: '10xl', styleValue: '128px' },
      { id: 'pe-11xl', name: '11xl', styleValue: '160px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'pe-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: '-pe-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-pe-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-pe-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-pe-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-pe-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-pe-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-pe-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-pe-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-pe-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-pe-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: '-pe-6xl', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: '-pe-7xl', name: '7xl', styleValue: '-64px', groupId: 'NEGATIVE' },
      { id: '-pe-8xl', name: '8xl', styleValue: '-80px', groupId: 'NEGATIVE' },
      { id: '-pe-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
      { id: '-pe-10xl', name: '10xl', styleValue: '-128px', groupId: 'NEGATIVE' },
      { id: '-pe-11xl', name: '11xl', styleValue: '-160px', groupId: 'NEGATIVE' },
    ],
    b: [
      { id: 'pb-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'pb-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'pb-xs', name: 'xs', styleValue: '4px' },
      { id: 'pb-sm', name: 'sm', styleValue: '6px' },
      { id: 'pb-md', name: 'md', styleValue: '8px' },
      { id: 'pb-lg', name: 'lg', styleValue: '12px' },
      { id: 'pb-xl', name: 'xl', styleValue: '16px' },
      { id: 'pb-2xl', name: '2xl', styleValue: '20px' },
      { id: 'pb-3xl', name: '3xl', styleValue: '24px' },
      { id: 'pb-4xl', name: '4xl', styleValue: '32px' },
      { id: 'pb-5xl', name: '5xl', styleValue: '40px' },
      { id: 'pb-6xl', name: '6xl', styleValue: '48px' },
      { id: 'pb-7xl', name: '7xl', styleValue: '64px' },
      { id: 'pb-8xl', name: '8xl', styleValue: '80px' },
      { id: 'pb-9xl', name: '9xl', styleValue: '96px' },
      { id: 'pb-10xl', name: '10xl', styleValue: '128px' },
      { id: 'pb-11xl', name: '11xl', styleValue: '160px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'pb-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: '-pb-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-pb-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-pb-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-pb-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-pb-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-pb-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-pb-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-pb-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-pb-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-pb-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: '-pb-6xl', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: '-pb-7xl', name: '7xl', styleValue: '-64px', groupId: 'NEGATIVE' },
      { id: '-pb-8xl', name: '8xl', styleValue: '-80px', groupId: 'NEGATIVE' },
      { id: '-pb-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
      { id: '-pb-10xl', name: '10xl', styleValue: '-128px', groupId: 'NEGATIVE' },
      { id: '-pb-11xl', name: '11xl', styleValue: '-160px', groupId: 'NEGATIVE' },
    ],
    l: [
      { id: 'ps-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
      { id: 'ps-xxs', name: 'xxs', styleValue: '2px' },
      { id: 'ps-xs', name: 'xs', styleValue: '4px' },
      { id: 'ps-sm', name: 'sm', styleValue: '6px' },
      { id: 'ps-md', name: 'md', styleValue: '8px' },
      { id: 'ps-lg', name: 'lg', styleValue: '12px' },
      { id: 'ps-xl', name: 'xl', styleValue: '16px' },
      { id: 'ps-2xl', name: '2xl', styleValue: '20px' },
      { id: 'ps-3xl', name: '3xl', styleValue: '24px' },
      { id: 'ps-4xl', name: '4xl', styleValue: '32px' },
      { id: 'ps-5xl', name: '5xl', styleValue: '40px' },
      { id: 'ps-6xl', name: '6xl', styleValue: '48px' },
      { id: 'ps-7xl', name: '7xl', styleValue: '64px' },
      { id: 'ps-8xl', name: '8xl', styleValue: '80px' },
      { id: 'ps-9xl', name: '9xl', styleValue: '96px' },
      { id: 'ps-10xl', name: '10xl', styleValue: '128px' },
      { id: 'ps-11xl', name: '11xl', styleValue: '160px' },
      ...(applicationType === ApplicationType.MOBILE
        ? [{ id: 'ps-safe', name: i18nextT('interfaces:Styles.Safe') }]
        : []),
      { id: '-ps-xxs', name: 'xxs', styleValue: '-2px', groupId: 'NEGATIVE' },
      { id: '-ps-xs', name: 'xs', styleValue: '-4px', groupId: 'NEGATIVE' },
      { id: '-ps-sm', name: 'sm', styleValue: '-6px', groupId: 'NEGATIVE' },
      { id: '-ps-md', name: 'md', styleValue: '-8px', groupId: 'NEGATIVE' },
      { id: '-ps-lg', name: 'lg', styleValue: '-12px', groupId: 'NEGATIVE' },
      { id: '-ps-xl', name: 'xl', styleValue: '-16px', groupId: 'NEGATIVE' },
      { id: '-ps-2xl', name: '2xl', styleValue: '-20px', groupId: 'NEGATIVE' },
      { id: '-ps-3xl', name: '3xl', styleValue: '-24px', groupId: 'NEGATIVE' },
      { id: '-ps-4xl', name: '4xl', styleValue: '-32px', groupId: 'NEGATIVE' },
      { id: '-ps-5xl', name: '5xl', styleValue: '-40px', groupId: 'NEGATIVE' },
      { id: '-ps-6xl', name: '6xl', styleValue: '-48px', groupId: 'NEGATIVE' },
      { id: '-ps-7xl', name: '7xl', styleValue: '-64px', groupId: 'NEGATIVE' },
      { id: '-ps-8xl', name: '8xl', styleValue: '-80px', groupId: 'NEGATIVE' },
      { id: '-ps-9xl', name: '9xl', styleValue: '-96px', groupId: 'NEGATIVE' },
      { id: '-ps-10xl', name: '10xl', styleValue: '-128px', groupId: 'NEGATIVE' },
      { id: '-ps-11xl', name: '11xl', styleValue: '-160px', groupId: 'NEGATIVE' },
    ],
  },
});

//todo: remove all - and hard coded options when custom input is developed
export const getStyleItemConfig = (
  i18nextT: TFunction,
  applicationType: ApplicationType | undefined,
) => {
  const isMobile = applicationType === ApplicationType.MOBILE;
  return {
    padding: getPaddingConfig(i18nextT, applicationType),
    margin: getMarginConfig(i18nextT, applicationType),
    legendsPadding: getPaddingConfig(i18nextT, applicationType),
    gap: {
      isDirectional: true,
      options: {
        all: [
          { id: 'gap-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'gap-xxs', name: 'xxs', styleValue: '2px' },
          { id: 'gap-xs', name: 'xs', styleValue: '4px' },
          { id: 'gap-sm', name: 'sm', styleValue: '6px' },
          { id: 'gap-md', name: 'md', styleValue: '8px' },
          { id: 'gap-lg', name: 'lg', styleValue: '12px' },
          { id: 'gap-xl', name: 'xl', styleValue: '16px' },
          { id: 'gap-2xl', name: '2xl', styleValue: '20px' },
          { id: 'gap-3xl', name: '3xl', styleValue: '24px' },
          { id: 'gap-4xl', name: '4xl', styleValue: '32px' },
          { id: 'gap-5xl', name: '5xl', styleValue: '40px' },
        ],
        x: [
          { id: 'gap-x-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'gap-x-xxs', name: 'xxs', styleValue: '2px' },
          { id: 'gap-x-xs', name: 'xs', styleValue: '4px' },
          { id: 'gap-x-sm', name: 'sm', styleValue: '6px' },
          { id: 'gap-x-md', name: 'md', styleValue: '8px' },
          { id: 'gap-x-lg', name: 'lg', styleValue: '12px' },
          { id: 'gap-x-xl', name: 'xl', styleValue: '16px' },
          { id: 'gap-x-2xl', name: '2xl', styleValue: '20px' },
          { id: 'gap-x-3xl', name: '3xl', styleValue: '24px' },
          { id: 'gap-x-4xl', name: '4xl', styleValue: '32px' },
          { id: 'gap-x-5xl', name: '5xl', styleValue: '40px' },
        ],
        y: [
          { id: 'gap-y-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'gap-y-xxs', name: 'xxs', styleValue: '2px' },
          { id: 'gap-y-xs', name: 'xs', styleValue: '4px' },
          { id: 'gap-y-sm', name: 'sm', styleValue: '6px' },
          { id: 'gap-y-md', name: 'md', styleValue: '8px' },
          { id: 'gap-y-lg', name: 'lg', styleValue: '12px' },
          { id: 'gap-y-xl', name: 'xl', styleValue: '16px' },
          { id: 'gap-y-2xl', name: '2xl', styleValue: '20px' },
          { id: 'gap-y-3xl', name: '3xl', styleValue: '24px' },
          { id: 'gap-y-4xl', name: '4xl', styleValue: '32px' },
          { id: 'gap-y-5xl', name: '5xl', styleValue: '40px' },
        ],
      },
    },
    // TODO: Remove this
    gapSecondary: {
      isDirectional: true,
      options: {
        all: [
          { id: 'gap-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'gap-xxs', name: 'xxs', styleValue: '2px' },
          { id: 'gap-xs', name: 'xs', styleValue: '4px' },
          { id: 'gap-sm', name: 'sm', styleValue: '6px' },
          { id: 'gap-md', name: 'md', styleValue: '8px' },
          { id: 'gap-lg', name: 'lg', styleValue: '12px' },
          { id: 'gap-xl', name: 'xl', styleValue: '16px' },
          { id: 'gap-2xl', name: '2xl', styleValue: '20px' },
          { id: 'gap-3xl', name: '3xl', styleValue: '24px' },
          { id: 'gap-4xl', name: '4xl', styleValue: '32px' },
          { id: 'gap-5xl', name: '5xl', styleValue: '40px' },
        ],
        x: [
          { id: 'gap-x-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'gap-x-xxs', name: 'xxs', styleValue: '2px' },
          { id: 'gap-x-xs', name: 'xs', styleValue: '4px' },
          { id: 'gap-x-sm', name: 'sm', styleValue: '6px' },
          { id: 'gap-x-md', name: 'md', styleValue: '8px' },
          { id: 'gap-x-lg', name: 'lg', styleValue: '12px' },
          { id: 'gap-x-xl', name: 'xl', styleValue: '16px' },
          { id: 'gap-x-2xl', name: '2xl', styleValue: '20px' },
          { id: 'gap-x-3xl', name: '3xl', styleValue: '24px' },
          { id: 'gap-x-4xl', name: '4xl', styleValue: '32px' },
          { id: 'gap-x-5xl', name: '5xl', styleValue: '40px' },
        ],
        y: [
          { id: 'gap-y-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'gap-y-xxs', name: 'xxs', styleValue: '2px' },
          { id: 'gap-y-xs', name: 'xs', styleValue: '4px' },
          { id: 'gap-y-sm', name: 'sm', styleValue: '6px' },
          { id: 'gap-y-md', name: 'md', styleValue: '8px' },
          { id: 'gap-y-lg', name: 'lg', styleValue: '12px' },
          { id: 'gap-y-xl', name: 'xl', styleValue: '16px' },
          { id: 'gap-y-2xl', name: '2xl', styleValue: '20px' },
          { id: 'gap-y-3xl', name: '3xl', styleValue: '24px' },
          { id: 'gap-y-4xl', name: '4xl', styleValue: '32px' },
          { id: 'gap-y-5xl', name: '5xl', styleValue: '40px' },
        ],
      },
    },
    boxShadow: {
      isDirectional: false,
      options: {
        all: [
          { id: 'shadow-none', name: i18nextT('interfaces:Styles.None') },
          { id: 'shadow-xs', name: 'xs' },
          { id: 'shadow-sm', name: 'sm' },
          { id: 'shadow-md', name: 'md' },
          { id: 'shadow-lg', name: 'lg' },
          { id: 'shadow-xl', name: 'xl' },
          { id: 'shadow-2xl', name: '2xl' },
          { id: 'shadow-3xl', name: '3xl' },
          { id: 'shadow-overflow-left', name: 'overflow-left' },
          { id: 'shadow-overflow-right', name: 'overflow-right' },
          { id: 'shadow-overflow-bottom', name: 'overflow-bottom' },
          { id: 'shadow-overflow-top', name: 'overflow-top' },
        ],
      },
    },
    borderWidth: {
      isDirectional: true,
      options: {
        all: [
          { id: 'border-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'border', name: 'sm', styleValue: '1px' },
          { id: 'border-2', name: 'md', styleValue: '2px' },
          { id: 'border-[3px]', name: 'lg', styleValue: '3px' },
          { id: 'border-4', name: 'xl', styleValue: '4px' },
        ],
        x: [
          { id: 'border-x-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'border-x', name: 'sm', styleValue: '1px' },
          { id: 'border-x-2', name: 'md', styleValue: '2px' },
          { id: 'border-x-[3px]', name: 'lg', styleValue: '3px' },
          { id: 'border-x-4', name: 'xl', styleValue: '4px' },
        ],
        y: [
          { id: 'border-y-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'border-y', name: 'sm', styleValue: '1px' },
          { id: 'border-y-2', name: 'md', styleValue: '2px' },
          { id: 'border-y-[3px]', name: 'lg', styleValue: '3px' },
          { id: 'border-y-4', name: 'xl', styleValue: '4px' },
        ],
        t: [
          { id: 'border-t-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'border-t', name: 'sm', styleValue: '1px' },
          { id: 'border-t-2', name: 'md', styleValue: '2px' },
          { id: 'border-t-[3px]', name: 'lg', styleValue: '3px' },
          { id: 'border-t-4', name: 'xl', styleValue: '4px' },
        ],
        r: [
          { id: 'border-e-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'border-e', name: 'sm', styleValue: '1px' },
          { id: 'border-e-2', name: 'md', styleValue: '2px' },
          { id: 'border-e-[3px]', name: 'lg', styleValue: '3px' },
          { id: 'border-e-4', name: 'xl', styleValue: '4px' },
        ],
        b: [
          { id: 'border-b-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'border-b', name: 'sm', styleValue: '1px' },
          { id: 'border-b-2', name: 'md', styleValue: '2px' },
          { id: 'border-b-[3px]', name: 'lg', styleValue: '3px' },
          { id: 'border-b-4', name: 'xl', styleValue: '4px' },
        ],
        l: [
          { id: 'border-s-0', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'border-s', name: 'sm', styleValue: '1px' },
          { id: 'border-s-2', name: 'md', styleValue: '2px' },
          { id: 'border-s-[3px]', name: 'lg', styleValue: '3px' },
          { id: 'border-s-4', name: 'xl', styleValue: '4px' },
        ],
      },
    },
    borderRadius: {
      isDirectional: true,
      options: {
        all: [
          { id: 'rounded-none', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'rounded-xxs', name: 'xxs', styleValue: '2px' },
          { id: 'rounded-xs', name: 'xs', styleValue: '4px' },
          { id: 'rounded-md', name: 'sm', styleValue: '8px' },
          { id: 'rounded-3xl', name: 'md', styleValue: '10px' },
          { id: 'rounded-5xl', name: 'lg', styleValue: '12px' },
          { id: 'rounded-full', name: 'full' },
        ],
        tl: [
          { id: 'rounded-ts-none', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'rounded-ts-xxs', name: 'xxs', styleValue: '2px' },
          { id: 'rounded-ts-xs', name: 'xs', styleValue: '4px' },
          { id: 'rounded-ts-md', name: 'sm', styleValue: '8px' },
          { id: 'rounded-ts-3xl', name: 'md', styleValue: '10px' },
          { id: 'rounded-ts-5xl', name: 'lg', styleValue: '12px' },
          { id: 'rounded-ts-full', name: 'full' },
        ],
        tr: [
          { id: 'rounded-te-none', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'rounded-te-xxs', name: 'xxs', styleValue: '2px' },
          { id: 'rounded-te-xs', name: 'xs', styleValue: '4px' },
          { id: 'rounded-te-md', name: 'sm', styleValue: '8px' },
          { id: 'rounded-te-3xl', name: 'md', styleValue: '10px' },
          { id: 'rounded-te-5xl', name: 'lg', styleValue: '12px' },
          { id: 'rounded-te-full', name: 'full' },
        ],
        bl: [
          { id: 'rounded-bs-none', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'rounded-bs-xxs', name: 'xxs', styleValue: '2px' },
          { id: 'rounded-bs-xs', name: 'xs', styleValue: '4px' },
          { id: 'rounded-bs-md', name: 'sm', styleValue: '8px' },
          { id: 'rounded-bs-3xl', name: 'md', styleValue: '10px' },
          { id: 'rounded-bs-5xl', name: 'lg', styleValue: '12px' },
          { id: 'rounded-bs-full', name: 'full' },
        ],
        br: [
          { id: 'rounded-be-none', name: i18nextT('interfaces:Styles.None'), styleValue: '0px' },
          { id: 'rounded-be-xxs', name: 'xxs', styleValue: '2px' },
          { id: 'rounded-be-xs', name: 'xs', styleValue: '4px' },
          { id: 'rounded-be-md', name: 'sm', styleValue: '8px' },
          { id: 'rounded-be-3xl', name: 'md', styleValue: '10px' },
          { id: 'rounded-be-5xl', name: 'lg', styleValue: '12px' },
          { id: 'rounded-be-full', name: 'full' },
        ],
      },
    },
    borderColor: {
      picker: 'color',
      conditionalStyles: {
        enabled: true,
      },
      isDirectional: false,
      colorGroups: borderColorGroups.map(({ id, label, colors }) => ({ id, label, colors })),
    },
    backgroundColor: {
      picker: 'color',
      conditionalStyles: {
        enabled: true,
      },
      isDirectional: false,
      isTabbedColorPicker: true,
      colorGroups: backgroundColorGroups.map(({ id, label, colors }) => ({ id, label, colors })),
    },
    chartHoverColor: {
      picker: 'color',
      conditionalStyles: {
        enabled: false,
      },
      isDirectional: false,
      colorGroups: chartColorGroups.map(({ id, label, colors }) => ({ id, label, colors })),
    },
    chartActiveColor: {
      picker: 'color',
      conditionalStyles: {
        enabled: false,
      },
      isDirectional: false,
      colorGroups: chartColorGroups.map(({ id, label, colors }) => ({ id, label, colors })),
    },
    rotation: {
      isDirectional: false,
      picker: 'input',
      getStyleObject: (value: string) => ({ transform: `rotate(${value})` }),
    },
    overflow: {
      isDirectional: true,
      options: {
        all: [
          { id: 'overflow-hidden', name: i18nextT('interfaces:Styles.Overflow.Hidden') },
          { id: 'overflow-scroll', name: i18nextT('interfaces:Styles.Overflow.Scroll') },
          ...(isMobile
            ? []
            : [
                { id: 'overflow-visible', name: i18nextT('interfaces:Styles.Overflow.Visible') },
                { id: 'overflow-auto', name: i18nextT('interfaces:Styles.Overflow.Auto') },
                { id: 'overflow-clip', name: i18nextT('interfaces:Styles.Overflow.Clip') },
              ]),
        ],
        x: [
          { id: 'overflow-x-hidden', name: i18nextT('interfaces:Styles.Overflow.Hidden') },
          { id: 'overflow-x-scroll', name: i18nextT('interfaces:Styles.Overflow.Scroll') },
          ...(isMobile
            ? []
            : [
                { id: 'overflow-x-visible', name: i18nextT('interfaces:Styles.Overflow.Visible') },
                { id: 'overflow-x-auto', name: i18nextT('interfaces:Styles.Overflow.Auto') },
                { id: 'overflow-x-clip', name: i18nextT('interfaces:Styles.Overflow.Clip') },
              ]),
        ],
        y: [
          { id: 'overflow-y-hidden', name: i18nextT('interfaces:Styles.Overflow.Hidden') },
          { id: 'overflow-y-scroll', name: i18nextT('interfaces:Styles.Overflow.Scroll') },
          ...(isMobile
            ? []
            : [
                { id: 'overflow-y-visible', name: i18nextT('interfaces:Styles.Overflow.Visible') },
                { id: 'overflow-y-auto', name: i18nextT('interfaces:Styles.Overflow.Auto') },
                { id: 'overflow-y-clip', name: i18nextT('interfaces:Styles.Overflow.Clip') },
              ]),
        ],
      },
    },
    minWidth: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: {
        all: [
          { id: 'none', name: i18nextT('interfaces:Styles.None') },
          { id: 'min-w-[200px]', name: 'xxs', styleValue: '200px' },
          { id: 'min-w-[360px]', name: 'xs', styleValue: '360px' },
          { id: 'min-w-[400px]', name: 'sm', styleValue: '400px' },
          { id: 'min-w-[540px]', name: 'md', styleValue: '540px' },
          { id: 'min-w-[640px]', name: 'lg', styleValue: '640px' },
          { id: 'min-w-[1024px]', name: 'xl', styleValue: '1024px' },
          { id: 'min-w-full', name: 'fill', styleValue: '100%' },
        ],
      },
    },
    minHeight: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: {
        all: [
          { id: 'none', name: i18nextT('interfaces:Styles.None') },
          { id: 'min-h-[200px]', name: 'xxs', styleValue: '200px' },
          { id: 'min-h-[360px]', name: 'xs', styleValue: '360px' },
          { id: 'min-h-[400px]', name: 'sm', styleValue: '400px' },
          { id: 'min-h-[540px]', name: 'md', styleValue: '540px' },
          { id: 'min-h-[640px]', name: 'lg', styleValue: '640px' },
          { id: 'min-h-[1024px]', name: 'xl', styleValue: '1024px' },
          { id: 'min-h-full', name: 'fill', styleValue: '100%' },
        ],
      },
    },
    maxWidth: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: {
        all: [
          { id: 'none', name: i18nextT('interfaces:Styles.None') },
          { id: 'max-w-[200px]', name: 'xxs', styleValue: '200px' },
          { id: 'max-w-[360px]', name: 'xs', styleValue: '360px' },
          { id: 'max-w-[400px]', name: 'sm', styleValue: '400px' },
          { id: 'max-w-[540px]', name: 'md', styleValue: '540px' },
          { id: 'max-w-[640px]', name: 'lg', styleValue: '640px' },
          { id: 'max-w-[1024px]', name: 'xl', styleValue: '1024px' },
          { id: 'max-w-full', name: 'fill', styleValue: '100%' },
        ],
      },
    },
    maxHeight: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      allowCustom: true,
      isDirectional: false,
      options: {
        all: [
          { id: 'none', name: i18nextT('interfaces:Styles.None') },
          { id: 'max-h-[200px]', name: 'xxs', styleValue: '200px' },
          { id: 'max-h-[360px]', name: 'xs', styleValue: '360px' },
          { id: 'max-h-[400px]', name: 'sm', styleValue: '400px' },
          { id: 'max-h-[540px]', name: 'md', styleValue: '540px' },
          { id: 'max-h-[640px]', name: 'lg', styleValue: '640px' },
          { id: 'max-h-[1024px]', name: 'xl', styleValue: '1024px' },
          { id: 'max-h-full', name: 'fill', styleValue: '100%' },
        ],
      },
    },
    width: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: {
        all: [
          { id: 'w-auto', name: i18nextT('interfaces:Styles.Auto'), styleValue: 'Auto' },
          { id: 'w-[200px]', name: 'xxs', styleValue: '200px' },
          { id: 'w-[360px]', name: 'xs', styleValue: '360px' },
          { id: 'w-[400px]', name: 'sm', styleValue: '400px' },
          { id: 'w-[540px]', name: 'md', styleValue: '540px' },
          { id: 'w-[640px]', name: 'lg', styleValue: '640px' },
          { id: 'w-[1024px]', name: 'xl', styleValue: '1024px' },
          { id: 'w-full', name: i18nextT('interfaces:Styles.Width.Fill') },
          ...(isMobile
            ? []
            : [
                { id: 'inherit', name: i18nextT('interfaces:Styles.Width.Inherit') },
                { id: 'w-fit', name: i18nextT('interfaces:Styles.Width.FitContent') },
              ]),
        ],
      },
    },
    dividerWidth: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: {
        all: [
          { id: 'w-[1px]', name: 'xxs', styleValue: '1px' },
          { id: 'w-[2px]', name: 'xs', styleValue: '2px' },
          { id: 'w-[3px]', name: 'sm', styleValue: '3px' },
          { id: 'w-[4px]', name: 'md', styleValue: '4px' },
        ],
      },
    },
    dividerHeight: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: {
        all: [
          { id: 'h-[1px]', name: 'xxs', styleValue: '1px' },
          { id: 'h-[2px]', name: 'xs', styleValue: '2px' },
          { id: 'h-[3px]', name: 'sm', styleValue: '3px' },
          { id: 'h-[4px]', name: 'md', styleValue: '4px' },
        ],
      },
    },
    height: {
      customStyles: {
        enabled: true,
        units: [
          { id: 'px', name: 'px' },
          { id: '%', name: '%' },
        ],
      },
      isDirectional: false,
      options: {
        all: [
          { id: 'h-auto', name: i18nextT('interfaces:Styles.Auto'), styleValue: 'Auto' },
          { id: 'h-[200px]', name: 'xxs', styleValue: '200px' },
          { id: 'h-[360px]', name: 'xs', styleValue: '360px' },
          { id: 'h-[400px]', name: 'sm', styleValue: '400px' },
          { id: 'h-[540px]', name: 'md', styleValue: '540px' },
          { id: 'h-[640px]', name: 'lg', styleValue: '640px' },
          { id: 'h-[1024px]', name: 'xl', styleValue: '1024px' },
          { id: 'h-full', name: i18nextT('interfaces:Styles.Height.Fill'), styleValue: '100%' },
        ],
      },
    },
    flexWrap: {
      isDirectional: false,
      options: {
        all: [
          { id: 'flex-nowrap', name: i18nextT('interfaces:Styles.FlexWrap.NoWrap') },
          { id: 'flex-wrap', name: i18nextT('interfaces:Styles.FlexWrap.Wrap') },
          { id: 'flex-wrap-reverse', name: i18nextT('interfaces:Styles.FlexWrap.WrapReverse') },
        ],
      },
    },
    layout: {
      isDirectional: false,
      options: {
        all: [
          { id: 'none', name: i18nextT('interfaces:Styles.TextDecoration.None') },
          { id: 'flex-shrink-0', name: i18nextT('interfaces:Styles.Layout.Hug') },
          { id: 'flex-shrink', name: i18nextT('interfaces:Styles.Layout.Contain') },
          { id: 'flex-grow', name: i18nextT('interfaces:Styles.Layout.Fill') },
          { id: 'self-start', name: i18nextT('interfaces:Styles.Layout.SelfStart') },
          { id: 'self-center', name: i18nextT('interfaces:Styles.Layout.SelfCenter') },
          { id: 'self-end', name: i18nextT('interfaces:Styles.Layout.SelfEnd') },
          { id: 'self-stretch', name: i18nextT('interfaces:Styles.Layout.SelfStretch') },
          { id: 'self-baseline', name: i18nextT('interfaces:Styles.Layout.SelfBaseline') },
          { id: 'self-auto', name: i18nextT('interfaces:Styles.Layout.SelfAuto') },
        ],
      },
    },
    textDecoration: {
      isDirectional: false,
      options: {
        all: [
          { id: 'none', name: i18nextT('interfaces:Styles.TextDecoration.None') },
          { id: 'underline', name: i18nextT('interfaces:Styles.TextDecoration.Underline') },
          { id: 'overline', name: i18nextT('interfaces:Styles.TextDecoration.Overline') },
          { id: 'line-through', name: i18nextT('interfaces:Styles.TextDecoration.LineThrough') },
          { id: 'no-underline', name: i18nextT('interfaces:Styles.TextDecoration.NoUnderline') },
        ],
      },
    },
    fontFamily: {
      isDirectional: false,
      options: {
        all: [
          {
            id: 'font-[family-name:var(--font-inter)]',
            name: i18nextT('interfaces:Styles.FontFamily.Inter'),
          },
          { id: 'font-mono', name: i18nextT('interfaces:Styles.FontFamily.Mono') },
          {
            id: 'font-[family-name:var(--font-source-serif-4)]',
            name: i18nextT('interfaces:Styles.FontFamily.SourceSerif4'),
          },
        ],
      },
    },
    backgroundImage: {
      isDirectional: false,
      picker: 'input',
    },
    backgroundSize: {
      isDirectional: false,
      options: {
        all: [
          { id: 'bg-auto', name: i18nextT('interfaces:Styles.BackgroundSize.Auto') },
          { id: 'bg-cover', name: i18nextT('interfaces:Styles.BackgroundSize.Cover') },
          { id: 'bg-contain', name: i18nextT('interfaces:Styles.BackgroundSize.Contain') },
          { id: 'bg-[length:100%_100%]', name: i18nextT('interfaces:Styles.BackgroundSize.Fit') },
        ],
      },
    },
    backgroundRepeat: {
      isDirectional: false,
      options: {
        all: [
          { id: 'bg-repeat', name: i18nextT('interfaces:Styles.BackgroundRepeat.Repeat') },
          { id: 'bg-no-repeat', name: i18nextT('interfaces:Styles.BackgroundRepeat.NoRepeat') },
          { id: 'bg-repeat-x', name: i18nextT('interfaces:Styles.BackgroundRepeat.RepeatX') },
          { id: 'bg-repeat-y', name: i18nextT('interfaces:Styles.BackgroundRepeat.RepeatY') },
        ],
      },
    },
    visibility: {
      isDirectional: false,
      options: {
        all: [
          { id: 'visible', name: i18nextT('interfaces:Styles.Visibility.Visible') },
          { id: 'invisible', name: i18nextT('interfaces:Styles.Visibility.Invisible') },
        ],
      },
    },
    position: {
      isDirectional: false,
      options: {
        all: [
          { id: 'static', name: i18nextT('interfaces:Styles.Position.Static') },
          ...(isMobile
            ? []
            : [{ id: 'fixed', name: i18nextT('interfaces:Styles.Position.Fixed') }]),
          { id: 'absolute', name: i18nextT('interfaces:Styles.Position.Absolute') },
          { id: 'relative', name: i18nextT('interfaces:Styles.Position.Relative') },
          ...(isMobile
            ? []
            : [{ id: 'sticky', name: i18nextT('interfaces:Styles.Position.Sticky') }]),
        ],
      },
    },
    breakAfter: {
      isDirectional: false,
      options: {
        all: [
          { id: 'break-after-auto', name: i18nextT('interfaces:Styles.Break.Auto') },
          { id: 'break-after-avoid', name: i18nextT('interfaces:Styles.Break.Avoid') },
          { id: 'break-after-page', name: i18nextT('interfaces:Styles.Break.Page') },
          { id: 'break-after-column', name: i18nextT('interfaces:Styles.Break.Column') },
        ],
      },
    },
    opacity: {
      isDirectional: false,
      options: {
        all: [
          { id: 'opacity-100', name: '100' },
          { id: 'opacity-0', name: '0' },
          { id: 'opacity-5', name: '5' },
          { id: 'opacity-10', name: '10' },
          { id: 'opacity-20', name: '20' },
          { id: 'opacity-25', name: '25' },
          { id: 'opacity-30', name: '30' },
          { id: 'opacity-40', name: '40' },
          { id: 'opacity-50', name: '50' },
          { id: 'opacity-60', name: '60' },
          { id: 'opacity-70', name: '70' },
          { id: 'opacity-75', name: '75' },
          { id: 'opacity-80', name: '80' },
          { id: 'opacity-90', name: '90' },
          { id: 'opacity-95', name: '95' },
        ],
      },
    },
  } as Record<
    string,
    {
      options?: Record<keyof AppearanceStyle, StyleSizeOption[]>;
      isDirectional: boolean;
      customStyles?: { enabled: boolean; units?: { id: string; name: string }[] };
      conditionalStyles?: { enabled: boolean };
      colorGroups?: ColorGroups[];
      picker?: 'color' | 'input';
      isTabbedColorPicker?: boolean;
      getStyleObject?: (value: string) => Record<string, string>;
    }
  >;
};
